import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { isDesktop } from '@trello/browser';
import { intl } from '@trello/i18n';
import type { PIIString } from '@trello/privacy';
import { RouterLink } from '@trello/router/router-link';
import { makeTemplateUrlSlug } from '@trello/urls';

import { BUTLER_POWER_UP_ID } from 'app/scripts/data/butler-id';
import { Auth } from 'app/scripts/db/Auth';
import { Breadcrumbs } from 'app/src/components/Breadcrumbs';
import { RelatedTemplates } from './RelatedTemplates';
import { SearchWrapper } from './SearchWrapper';
import { TemplatePreview } from './TemplatePreview';
import { TemplatesPowerUp } from './TemplatesPowerUp';
import type { TemplatesStoryQuery } from './TemplatesStoryQuery.generated';
import { TemplateStoryAbout } from './TemplateStoryAbout';
import { TemplateStoryHeader } from './TemplateStoryHeader';
import type { Plugin, Template } from './types';

import styles from './TemplateStoryContent.less';

type Board = NonNullable<NonNullable<TemplatesStoryQuery>['board']>;

export const TemplateStoryContent: FunctionComponent<{
  category: string;
  board: Board | null;
  shortLink: string;
  isInAppGallery: boolean;
  username?: PIIString;
  templates: Template[];
  onMount: () => void;
}> = ({
  category,
  board,
  shortLink,
  isInAppGallery,
  username,
  templates,
  onMount,
}) => {
  useEffect(() => {
    onMount();
  });

  const relatedTemplates = board?.related;
  const templateGallery = board ? board.templateGallery : null;
  const boardId = board ? board.id : null;
  const templateName = board ? board.name : null;

  const plugins = (board ? board.plugins : [])
    .filter(
      ({ id, moderatedState }): boolean =>
        // Don't show Butler or moderated/hidden plugins in this list
        id !== BUTLER_POWER_UP_ID && moderatedState === null,
    )
    .map(
      ({ icon, id, listing }): Plugin => ({
        icon: icon.url,
        id,
        name: listing ? listing.name : '',
        overview: listing ? listing.overview : '',
        description: listing ? listing.description : '',
      }),
    );

  const shouldShowBCUpgrade =
    plugins.length > 1 &&
    !Auth.me()?.get('enterprises')?.length &&
    !isDesktop();

  const storyPageUrl = `/templates/${makeTemplateUrlSlug(
    category,
    templateName || '',
    shortLink,
  )}`;

  return (
    <div>
      <div className={styles.templateBreadCrumb}>
        <Breadcrumbs
          items={[
            {
              title: intl.formatMessage({
                id: 'templates.templates.template-gallery',
                defaultMessage: 'Template gallery',
                description: 'The title of the template gallery',
              }),
              href: '/templates',
            },
            {
              title: intl
                .formatMessage({
                  id: `templates.templates.${category}`,
                  defaultMessage: 'Category',
                  description: 'A header for templates by a specific category',
                })
                .replace(/&amp;/g, '&'),
              href: `/templates/${category}`,
            },
            {
              title: templateName || '',
              href: storyPageUrl,
            },
          ]}
        />
        {isInAppGallery && <SearchWrapper templates={templates} />}
      </div>

      {board?.id && (
        <>
          <TemplateStoryHeader
            avatarShape={templateGallery?.avatarShape || ''}
            avatarUrl={templateGallery?.avatarUrl || ''}
            boardId={boardId}
            byline={templateGallery?.byline || ''}
            copyCount={templateGallery?.stats?.copyCount || 0}
            shortLink={shortLink}
            templateName={templateName || ''}
            shouldShowBCUpgrade={shouldShowBCUpgrade}
            username={username}
            storyPageUrl={storyPageUrl}
            viewCount={templateGallery?.stats?.viewCount || 0}
          />
          <TemplateStoryAbout desc={board ? board.desc : ''} />
          <TemplatePreview boardId={board.id} />
          <div className={styles.viewTemplate}>
            <RouterLink title={templateName || ''} href={`/b/${shortLink}`}>
              <FormattedMessage
                id="templates.templates.view-template"
                defaultMessage="View template"
                description="The button to view the selected template"
              />
            </RouterLink>
          </div>
        </>
      )}

      {plugins && plugins.length > 0 && (
        <TemplatesPowerUp
          plugins={plugins}
          shouldShowBCUpgrade={shouldShowBCUpgrade}
        />
      )}

      {relatedTemplates && relatedTemplates.length > 0 && (
        <RelatedTemplates templates={relatedTemplates} />
      )}
    </div>
  );
};
